// Contract: Posting Details rules — candidate sections, interview filter, copy (GOTU-8150).
// Sources: merged:TempMee/recruitment-service@dbf769b33 (officeStatus values; metadata.tabs keys,
//          http/types/candidate_list.go TabMetadata — no `cancelled` key);
//          merged:TempMee/dental-portal@b68f1a1fc + merged:TempMee/mobile-app@6109e549b (CandidateStatus and
//          OnsiteInterviewStatus member names, identical in both; OnsiteInterviewStatusMapper.fromJobStatus buckets);
//          ticket:GOTU-8150 + Figma IRnR1z98UraThG8BdvffK7 node 7113:61654 (section order and titles, filter
//          labels and order, menu rows, copy).
// The job statuses behind each filter are NOT here: each app's OnsiteInterviewStatus → job-status mapping owns
// them (web: OnsiteInterviewStatusMapper.toJobStatus and the adapter's `all` = counter,pending,open,filled).
// One file, two verbatim copies — change both together:
//   TempMee/dental-portal src/components/PermanentJobsV2/PostingDetails/postingDetailsRules.ts
//   TempMee/mobile-app    src/components/dentist/PermanentJobs/PostingDetails/postingDetailsRules.ts

import { CandidateStatus, OnsiteInterviewStatus } from '~/domain/PermanentJobs';

export type TCandidateSectionKey = 'new' | 'scheduled' | 'interviewed' | 'rejected' | 'hired';

export interface ICandidateSection {
  key: TCandidateSectionKey;
  title: string;
  /** The candidate statuses the section lists — also the list request's `status` filter. */
  statuses: CandidateStatus[];
}

/**
 * The Candidates tab's sections, in the order the page draws them. `app_expired` and
 * `cancelled` candidates belong to none of them.
 */
export const CANDIDATE_SECTIONS: readonly ICandidateSection[] = [
  { key: 'new', title: 'New', statuses: [CandidateStatus.New, CandidateStatus.Applied] },
  { key: 'scheduled', title: 'Scheduled', statuses: [CandidateStatus.Scheduled] },
  { key: 'interviewed', title: 'Interviewed', statuses: [CandidateStatus.Interviewed] },
  { key: 'rejected', title: 'Rejected', statuses: [CandidateStatus.Rejected] },
  { key: 'hired', title: 'Hired', statuses: [CandidateStatus.Hired] },
];

/** The sections that hold at least one candidate, going by the list's per-status counts. */
export const visibleCandidateSections = (
  counts: Partial<Record<CandidateStatus, number>>,
): ICandidateSection[] =>
  CANDIDATE_SECTIONS.filter(({ statuses }) => statuses.some((status) => (counts[status] ?? 0) > 0));

/**
 * The Interviews tab's status filter, in the Figma's order. The Figma's "Today" option is
 * left out: the interviews endpoint has no date filter.
 */
export const INTERVIEW_FILTERS = [
  { value: 'all', label: 'All Upcoming' },
  { value: OnsiteInterviewStatus.ActionNeeded, label: 'Confirm Candidates' },
  { value: OnsiteInterviewStatus.Open, label: 'Open' },
  { value: OnsiteInterviewStatus.Scheduled, label: 'Scheduled' },
  { value: OnsiteInterviewStatus.Cancelled, label: 'Expired & Cancelled' },
  { value: OnsiteInterviewStatus.Completed, label: 'Complete' },
] as const;

export type TInterviewFilterValue = (typeof INTERVIEW_FILTERS)[number]['value'];

export const DEFAULT_INTERVIEW_FILTER: TInterviewFilterValue = 'all';

/** The label of a filter value, e.g. `'all'` → "All Upcoming". Unknown values read as the default. */
export const interviewFilterLabel = (value: 'all' | OnsiteInterviewStatus): string =>
  INTERVIEW_FILTERS.find((filter) => filter.value === value)?.label ?? INTERVIEW_FILTERS[0].label;

/** What an Interviews-tab card offers, by its status bucket: the actions the dashboard's shift lists give an interview. */
export interface IInterviewCardActions {
  /** The envelope: invite the posting's applicants to this interview. */
  invite: boolean;
  edit: boolean;
  message: boolean;
  cancel: boolean;
}

const CANCELLABLE_INTERVIEW_STATUSES: readonly OnsiteInterviewStatus[] = [
  OnsiteInterviewStatus.Open,
  OnsiteInterviewStatus.Scheduled,
  OnsiteInterviewStatus.ActionNeeded,
];

export const interviewCardActions = (status: OnsiteInterviewStatus): IInterviewCardActions => ({
  invite: status === OnsiteInterviewStatus.Open,
  edit: status === OnsiteInterviewStatus.Open,
  message: status === OnsiteInterviewStatus.Scheduled,
  cancel: CANCELLABLE_INTERVIEW_STATUSES.includes(status),
});

export const POSTING_DETAILS_COPY = {
  title: 'Posting Details',
  actionsLabel: 'Posting actions',
  editPosting: 'Edit posting',
  addInterview: 'Add interview',
  closePosting: 'Close posting',
  candidatesTab: 'Candidates',
  interviewsTab: 'Interviews',
  jobOverview: {
    title: 'Title',
    profession: 'Profession',
    posted: 'Posted',
    location: 'Location',
  },
  jobDescription: 'Job Description',
  applied: 'Applied',
  year: 'Year',
  filter: {
    title: 'Filter',
    status: 'Status',
    open: 'Filter interviews',
    close: 'Close filter',
  },
  noInterviews: 'No interviews match this filter.',
} as const;
